import { DateTime, DurationUnits, ToRelativeUnit } from 'luxon';
import i18n from 'src/i18n';

const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

const dateOptions: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
  year: '2-digit',
};

const monthAndDayOptions: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
};

const timeOptions: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
};

const timeAndTZOptions: Intl.DateTimeFormatOptions = {
  ...timeOptions,
  timeZoneName: 'short',
};

const monthAbbrOptions: Intl.DateTimeFormatOptions = { month: 'short' };

const monthFullOptions: Intl.DateTimeFormatOptions = { month: 'long' };

const weekdayMonthDayOptions: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: '2-digit',
};

const longDateTimeOptions: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

// The locale is read at call time (not module load time) so formatting
// follows the user's current i18n language, including changes made after
// a DateTime instance was created.
//
// Modern ICU (browsers, newer Node) emits a narrow no-break space (U+202F)
// before AM/PM; older ICU emits an ASCII space. Normalize to an ASCII space
// so output is identical across environments and matches the legacy
// hardcoded-format output.
const toLocaleString = (dt: DateTime, options: Intl.DateTimeFormatOptions) =>
  dt
    .setLocale(i18n.language)
    .toLocaleString(options)
    .replace(/\u202f/g, ' ');

export const createDateTimeFromJSDate = (date: Date) =>
  DateTime.fromJSDate(date, {
    zone: timeZone,
  });

export const createDateTimeFromISO = (date: string) =>
  DateTime.fromISO(date, {
    zone: timeZone,
    locale: i18n.language,
  });

export const formatTimeAndTZ = (dt: DateTime) =>
  toLocaleString(dt, timeAndTZOptions);

export const formatTimeAndTZFromString = (dtString: string) => {
  const dt = createDateTimeFromISO(dtString);
  return formatTimeAndTZ(dt);
};

export const formatTime = (dt: DateTime) => toLocaleString(dt, timeOptions);

export const formatMonthAndDay = (dt: DateTime) =>
  toLocaleString(dt, monthAndDayOptions);

export const formatMonthAbbr = (dt: DateTime) =>
  toLocaleString(dt, monthAbbrOptions);

export const formatMonthFull = (dt: DateTime) =>
  toLocaleString(dt, monthFullOptions);

export const formatDate = (dt: DateTime) => toLocaleString(dt, dateOptions);

export const formatDateFromString = (dtString: string) => {
  const dt = createDateTimeFromISO(dtString);
  return formatDate(dt);
};

// Composed from the date and time parts (rather than one combined Intl call)
// to preserve the pre-existing "MM/dd/yy h:mm a ZZZZ" shape for en-US, which
// a combined call would join with an extra comma.
export const formatFull = (dt: DateTime) =>
  `${formatDate(dt)} ${formatTimeAndTZ(dt)}`;

export const formatWeekdayMonthDay = (dt: DateTime) =>
  toLocaleString(dt, weekdayMonthDayOptions);

export const formatLongDateTime = (dt: DateTime) =>
  toLocaleString(dt, longDateTimeOptions);

export const areSameDay = (dt1: DateTime, dt2: DateTime) =>
  dt1.hasSame(dt2, 'day');

export const getTimeRange = (start: string, end: string) => {
  const startDt = createDateTimeFromISO(start);
  const endDt = createDateTimeFromISO(end);

  const sameDay = areSameDay(startDt, endDt);
  const endFormatted = sameDay ? formatTimeAndTZ(endDt) : formatFull(endDt);
  const startFormatted = formatFull(startDt);

  return `${startFormatted} to ${endFormatted}`;
};

export const getDay = (dt: DateTime) => dt.weekdayShort;

export const getMonthAndDayFromDate = (date: string) =>
  formatMonthAndDay(createDateTimeFromISO(date).setZone('UTC'));

export const getTimeSinceDate = (dateString: string) => {
  const units = [
    'years',
    'months',
    'days',
    'hours',
    'minutes',
    'seconds',
  ] as DurationUnits;
  const date = createDateTimeFromISO(dateString);
  const now = DateTime.now();
  const duration = now.diff(date, units);

  return now.minus(duration).toRelative({
    unit: units as ToRelativeUnit,
    locale: i18n.language,
    style: 'narrow',
  });
};
